import { submitImpactCo2Answer } from '@/domain/impact-co2/actions/submit-impact-co2-answer.action';
import {
  ImpactCo2Answer,
  ValuesCargo,
  ValuesMobility,
} from '@/domain/impact-co2/entities/question.entity';
import { PositionGeocode } from '@/domain/position-geocode/position-geocode.entity';
import { calculateDistanceBetweenCoordinates } from '@/domain/position-geocode/utils/calculate-distance/calculate-distance';
import { createAppAsyncThunk } from '@/domain/store.thunk';

import {
  TourTravel,
  TourTravelConnexionTypeWithEvent,
} from '../entities/project-tour-ontour-travel.entity';
import { selectProjectTourOnTourEvents } from '../project-tour-ontour.selectors';
import { addTourTravel } from './add-tour-travel.action';
import { editTourTravel } from './edit-tour-travel.action';

type Params = {
  sourceTourTravel: TourTravel;
  sequenceId: number;
  fromTourEventId: number | undefined;
  toTourEventId: number | undefined;
};

export const copyTourTravel = createAppAsyncThunk(
  'projectTourOntour/copyTourTravel',
  async (
    params: Params,
    { extra: { impactCo2Gateway }, dispatch, getState },
  ): Promise<void> => {
    const targetConnexionType = resolveConnexionType(
      params.fromTourEventId,
      params.toTourEventId,
    );
    const externalPosition = extractExternalPosition(params.sourceTourTravel);

    const newTravel = await dispatch(
      addTourTravel({
        sequenceId: params.sequenceId,
        tourTravel: {
          name: params.sourceTourTravel.name,
          connexionTypeWithEvent: targetConnexionType,
          travelDistance: 0,
          fromEventId: params.fromTourEventId,
          fromPosition:
            targetConnexionType === 'incoming' ? externalPosition : undefined,
          toEventId: params.toTourEventId,
          toPosition:
            targetConnexionType === 'outgoing' ? externalPosition : undefined,
        },
      }),
    ).unwrap();

    const tourEvents = selectProjectTourOnTourEvents(getState());
    const fromEvent = tourEvents.find((e) => e.id === params.fromTourEventId);
    const toEvent = tourEvents.find((e) => e.id === params.toTourEventId);

    const travelDistance = await calculateTravelDistance(
      targetConnexionType,
      externalPosition,
      fromEvent?.position?.coordinates,
      toEvent?.position?.coordinates,
    );

    if (travelDistance > 0) {
      await dispatch(
        editTourTravel({
          tourTravel: { ...newTravel, travelDistance },
        }),
      );
    }

    const sourceAnswers = await impactCo2Gateway.getImpactAnswers(
      params.sourceTourTravel.id,
    );
    const answersToCreate: ImpactCo2Answer[] = sourceAnswers
      .filter((a) => a.category === 'MOBILITY' || a.category === 'CARGO')
      .map((answer) => {
        switch (answer.category) {
          case 'MOBILITY': {
            const a = answer as ImpactCo2Answer<ValuesMobility>;
            return {
              ...a,
              values: { ...a.values, travelDistance },
            };
          }
          case 'CARGO': {
            const a = answer as ImpactCo2Answer<ValuesCargo>;
            return {
              ...a,
              values: { ...a.values, travelDistance },
            };
          }
          default:
            throw new Error('Unexpected answer category');
        }
      });

    for (const answer of answersToCreate) {
      dispatch(
        submitImpactCo2Answer({
          answer,
          projectId: newTravel.id,
          submitMode: 'create',
        }),
      );
    }
  },
);

async function calculateTravelDistance(
  connexionType: TourTravelConnexionTypeWithEvent,
  externalPosition: PositionGeocode | undefined,
  fromEventCoords: [number, number] | undefined,
  toEventCoords: [number, number] | undefined,
): Promise<number> {
  switch (connexionType) {
    case 'incoming':
      return calculateDistanceBetweenCoordinates(
        externalPosition?.coordinates,
        toEventCoords,
      );
    case 'outgoing':
      return calculateDistanceBetweenCoordinates(
        fromEventCoords,
        externalPosition?.coordinates,
      );
    case 'direct':
      return calculateDistanceBetweenCoordinates(
        fromEventCoords,
        toEventCoords,
      );
  }
}

function resolveConnexionType(
  fromTourEventId: number | undefined,
  toTourEventId: number | undefined,
): TourTravelConnexionTypeWithEvent {
  if (fromTourEventId !== undefined && toTourEventId !== undefined) {
    return 'direct';
  }
  if (fromTourEventId !== undefined) return 'outgoing';
  return 'incoming';
}

function extractExternalPosition(
  source: TourTravel,
): PositionGeocode | undefined {
  if (source.connexionTypeWithEvent === 'incoming') return source.fromPosition;
  if (source.connexionTypeWithEvent === 'outgoing') return source.toPosition;
  return undefined;
}
